import React, { useState, useEffect } from 'react';
import { X, Search, Plus, ArrowLeft, Package, ChevronRight, Grid, List } from 'lucide-react';
import apiService from '../../services/api';

const ProductSelectorModal = ({ onClose, onAddProducts }) => {
    const [currentView, setCurrentView] = useState('categories'); // 'categories' | 'products'
    const [categories, setCategories] = useState({});
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

    // Load categories when modal opens
    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setIsLoading(true);
        try {
            console.log('📚 Loading product categories...');
            const response = await apiService.getProductCategories();
            
            if (response.success) {
                setCategories(response.data || {});
                console.log('✅ Categories loaded:', Object.keys(response.data || {}).length, 'categories');
            } else {
                console.error('❌ Failed to load categories:', response.message);
            }
        } catch (error) {
            console.error('💥 Error loading categories:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadProductsForCategory = async (category, subcategory = '') => {
        setIsLoading(true);
        try {
            console.log('📦 Loading products for:', { category, subcategory });
            
            const params = {
                category: category,
                subcategory: subcategory,
                limit: 100000 // Load more since we're browsing a specific category
            };

            const response = await apiService.getQuotationProducts(params);
            
            if (response.success) {
                setProducts(response.data || []);
                console.log(`✅ Loaded ${response.data?.length || 0} products for ${category}${subcategory ? ` > ${subcategory}` : ''}`);
            } else {
                console.error('❌ Failed to load products:', response.message);
                setProducts([]);
            }
        } catch (error) {
            console.error('💥 Error loading products:', error);
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setSelectedSubcategory('');
        setCurrentView('subcategories');
    };

    const handleSubcategorySelect = (subcategory) => {
        setSelectedSubcategory(subcategory);
        loadProductsForCategory(selectedCategory, subcategory);
        setCurrentView('products');
    };

    const handleBackToCategories = () => {
        setCurrentView('categories');
        setSelectedCategory('');
        setSelectedSubcategory('');
        setProducts([]);
    };

    const handleBackToSubcategories = () => {
        setCurrentView('subcategories');
        setSelectedSubcategory('');
        setProducts([]);
    };

    const toggleProductSelection = (product) => {
        setSelectedProducts(prev => {
            const isSelected = prev.find(p => p._id === product._id || p.productId === product.productId);
            if (isSelected) {
                return prev.filter(p => p._id !== product._id && p.productId !== product.productId);
            } else {
                return [...prev, product];
            }
        });
    };

    const isProductSelected = (product) => {
        return selectedProducts.find(p => p._id === product._id || p.productId === product.productId);
    };

    const handleAddSelectedProducts = () => {
        if (selectedProducts.length > 0) {
            console.log('➕ Adding products to quotation:', selectedProducts);
            onAddProducts(selectedProducts);
            onClose();
        }
    };

    const getFilteredProducts = () => {
        if (!searchTerm) return products;
        return products.filter(product => 
            (product.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.productId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.barcode || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const renderCategoriesView = () => (
        <div style={{ padding: '20px 24px' }}>
            <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '16px'
            }}>
                Select Product Category
            </h3>
            <p style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '24px'
            }}>
                Choose a category to browse available products
            </p>

            {isLoading ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px'
                }}>
                    <div style={{ textAlign: 'center', color: '#6b7280' }}>
                        <Package size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                        <p>Loading categories...</p>
                    </div>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '16px'
                }}>
                    {Object.entries(categories).map(([categoryName, subcategories]) => (
                        <div
                            key={categoryName}
                            onClick={() => handleCategorySelect(categoryName)}
                            style={{
                                padding: '20px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                transition: 'all 0.2s ease',
                                position: 'relative'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.borderColor = '#1e40af';
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.backgroundColor = 'white';
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                marginBottom: '12px'
                            }}>
                                <h4 style={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#1f2937',
                                    margin: 0,
                                    lineHeight: '1.4'
                                }}>
                                    {categoryName}
                                </h4>
                                <ChevronRight size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
                            </div>
                            <p style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                margin: '0 0 12px 0'
                            }}>
                                {Array.isArray(subcategories) ? subcategories.length : 0} subcategories
                            </p>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '6px'
                            }}>
                                {Array.isArray(subcategories) && subcategories.slice(0, 3).map((sub) => (
                                    <span
                                        key={sub.name}
                                        style={{
                                            fontSize: '11px',
                                            padding: '2px 6px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#6b7280',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        {sub.name}
                                    </span>
                                ))}
                                {Array.isArray(subcategories) && subcategories.length > 3 && (
                                    <span style={{
                                        fontSize: '11px',
                                        padding: '2px 6px',
                                        backgroundColor: '#e5e7eb',
                                        color: '#6b7280',
                                        borderRadius: '4px'
                                    }}>
                                        +{subcategories.length - 3} more
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderSubcategoriesView = () => {
        const subcategories = categories[selectedCategory] || [];
        
        return (
            <div style={{ padding: '20px 24px' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                }}>
                    <button
                        onClick={handleBackToCategories}
                        style={{
                            padding: '8px',
                            backgroundColor: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: 0
                        }}>
                            {selectedCategory}
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            margin: 0
                        }}>
                            Select a subcategory to view products
                        </p>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '12px'
                }}>
                    {subcategories.map((subcategory) => (
                        <div
                            key={subcategory.name}
                            onClick={() => handleSubcategorySelect(subcategory.name)}
                            style={{
                                padding: '16px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.borderColor = '#1e40af';
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.backgroundColor = 'white';
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <h4 style={{
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#1f2937',
                                    margin: 0
                                }}>
                                    {subcategory.name}
                                </h4>
                                <ChevronRight size={16} style={{ color: '#9ca3af' }} />
                            </div>
                            {subcategory.code && (
                                <p style={{
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    margin: '4px 0 0 0'
                                }}>
                                    Code: {subcategory.code}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderProductsView = () => {
        const filteredProducts = getFilteredProducts();

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Products Header */}
                <div style={{
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid #e5e7eb'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px'
                    }}>
                        <button
                            onClick={handleBackToSubcategories}
                            style={{
                                padding: '8px',
                                backgroundColor: '#f3f4f6',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <ArrowLeft size={16} />
                        </button>
                        <div style={{ flex: 1 }}>
                            <h3 style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#1f2937',
                                margin: 0
                            }}>
                                {selectedCategory} → {selectedSubcategory}
                            </h3>
                            <p style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                margin: 0
                            }}>
                                {filteredProducts.length} products available
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setViewMode('grid')}
                                style={{
                                    padding: '8px',
                                    backgroundColor: viewMode === 'grid' ? '#1e40af' : '#f3f4f6',
                                    color: viewMode === 'grid' ? 'white' : '#6b7280',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                <Grid size={16} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                style={{
                                    padding: '8px',
                                    backgroundColor: viewMode === 'list' ? '#1e40af' : '#f3f4f6',
                                    color: viewMode === 'list' ? 'white' : '#6b7280',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                <List size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div style={{ position: 'relative' }}>
                        <Search
                            size={16}
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px 8px 36px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                        />
                    </div>
                </div>

                {/* Products List */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '16px 24px'
                }}>
                    {isLoading ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '40px'
                        }}>
                            <div style={{ textAlign: 'center', color: '#6b7280' }}>
                                <Package size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                                <p>Loading products...</p>
                            </div>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '40px'
                        }}>
                            <div style={{ textAlign: 'center', color: '#6b7280' }}>
                                <Package size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                                <p>No products found</p>
                                {searchTerm && (
                                    <p style={{ fontSize: '14px', marginTop: '8px' }}>
                                        Try adjusting your search terms
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '12px'
                        }}>
                            {filteredProducts.map((product, index) => (
                                <div
                                    key={product._id || product.productId || index}
                                    onClick={() => toggleProductSelection(product)}
                                    style={{
                                        padding: '16px',
                                        border: `2px solid ${isProductSelected(product) ? '#1e40af' : '#e5e7eb'}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: isProductSelected(product) ? '#eff6ff' : 'white',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                    }}>
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            border: `2px solid ${isProductSelected(product) ? '#1e40af' : '#d1d5db'}`,
                                            borderRadius: '4px',
                                            backgroundColor: isProductSelected(product) ? '#1e40af' : 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            marginTop: '2px'
                                        }}>
                                            {isProductSelected(product) && (
                                                <svg width="10" height="10" viewBox="0 0 12 12" fill="white">
                                                    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#1f2937',
                                                margin: '0 0 8px 0',
                                                lineHeight: '1.4'
                                            }}>
                                                {product.description || 'Unnamed Product'}
                                            </h4>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                <p style={{
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    <strong>Product ID:</strong> {product.productId || 'N/A'}
                                                </p>
                                                {product.barcode && (
                                                    <p style={{
                                                        fontSize: '12px',
                                                        color: '#6b7280',
                                                        margin: 0
                                                    }}>
                                                        <strong>Barcode:</strong> {product.barcode}
                                                    </p>
                                                )}
                                                <p style={{
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    <strong>Category:</strong> {product.subcategory || product.category}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        // List view
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {filteredProducts.map((product, index) => (
                                <div
                                    key={product._id || product.productId || index}
                                    onClick={() => toggleProductSelection(product)}
                                    style={{
                                        padding: '12px 16px',
                                        border: `2px solid ${isProductSelected(product) ? '#1e40af' : '#e5e7eb'}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: isProductSelected(product) ? '#eff6ff' : 'white',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}
                                >
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        border: `2px solid ${isProductSelected(product) ? '#1e40af' : '#d1d5db'}`,
                                        borderRadius: '4px',
                                        backgroundColor: isProductSelected(product) ? '#1e40af' : 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        {isProductSelected(product) && (
                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="white">
                                                <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '16px', alignItems: 'center' }}>
                                        <div>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#1f2937',
                                                margin: 0
                                            }}>
                                                {product.description || 'Unnamed Product'}
                                            </h4>
                                        </div>
                                        <div>
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                margin: 0
                                            }}>
                                                {product.productId || 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                margin: 0
                                            }}>
                                                {product.barcode || 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                margin: 0
                                            }}>
                                                {product.subcategory || product.category || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '1200px',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: '0 0 4px 0'
                        }}>
                            {currentView === 'categories' ? 'Browse Categories' : 
                             currentView === 'subcategories' ? 'Select Subcategory' : 
                             'Select Products'}
                        </h2>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            margin: 0
                        }}>
                            {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#6b7280'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                    {currentView === 'categories' && renderCategoriesView()}
                    {currentView === 'subcategories' && renderSubcategoriesView()}
                    {currentView === 'products' && renderProductsView()}
                </div>

                {/* Footer */}
                {selectedProducts.length > 0 && (
                    <div style={{
                        padding: '16px 24px',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{
                            fontSize: '14px',
                            color: '#6b7280'
                        }}>
                            {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} ready to add
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: '12px'
                        }}>
                            <button
                                onClick={() => setSelectedProducts([])}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: 'white',
                                    color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Clear Selection
                            </button>
                            <button
                                onClick={handleAddSelectedProducts}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#1e40af',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <Plus size={16} />
                                Add to Quotation
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductSelectorModal;